import {
  BasicModal,
  Box,
  Button,
  CircularProgress,
  Icon,
  StaticDateRangePicker,
  TextField,
  Theme,
  Typography
} from "@outschool/backpack";
import {
  PauseSubscriptionMutation,
  PauseSubscriptionMutationVariables,
  SubscriptionCreditBalanceQuery
} from "@outschool/gql-frontend-generated";
import { faInfoCircle } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { dayjs, formatShortDate } from "@outschool/time";
import { useTrackEvent } from "@outschool/ui-analytics";
import { ApolloQueryResult, gql, useMutation } from "@outschool/ui-apollo";
import React from "react";

import { sleep } from "../../../shared/helpers";
import { useTimeZone } from "../../providers/TimeZoneProvider";

export const pauseSubscriptionMutation = gql`
  mutation PauseSubscription($willResumeAt: DateTime) {
    pauseSubscription(willResumeAt: $willResumeAt)
  }
`;

export const resumeSubscriptionMutation = gql`
  mutation ResumeSubscription {
    resumeSubscription
  }
`;

export interface PauseMembershipModalProps {
  isOpen: boolean;
  onClose: () => void;
  nextBillingDate: Date;
  refetchSubscriptionCreditBalance: () => Promise<
    ApolloQueryResult<SubscriptionCreditBalanceQuery>
  >;
  willResumeAt: Date | null;
  numPauseRemaining: number;
}

export default function PauseMembershipModal({
  isOpen,
  onClose,
  nextBillingDate,
  refetchSubscriptionCreditBalance,
  willResumeAt,
  numPauseRemaining
}: PauseMembershipModalProps) {
  const { timeZone } = useTimeZone();
  const { t } = useTranslation(
    "client\\components\\subscriptions\\PauseMembershipModal"
  );
  const trackEvent = useTrackEvent();
  // Placeholder state for date range
  const [pausing, setPausing] = React.useState(false);
  const [resumeDate, setResumeDate] = React.useState<Date | null>(willResumeAt);
  const [pauseSubscription] = useMutation<
    PauseSubscriptionMutation,
    PauseSubscriptionMutationVariables
  >(pauseSubscriptionMutation);

  const handleSave = async () => {
    try {
      setPausing(true);
      let retries = 1;
      await pauseSubscription({
        variables: {
          willResumeAt: resumeDate
        }
      });
      trackEvent("subscription_paused", {
        willResumeAt: resumeDate
      });
      while (retries <= 3) {
        await sleep(1000);
        const { data } = await refetchSubscriptionCreditBalance();
        if (data.subscriptionCreditBalance?.pausedAt) {
          break;
        }
        retries++;
      }
      onClose();
      setPausing(false);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <BasicModal
      open={isOpen}
      onClose={onClose}
      hasCloseButton={true}
      closeButtonArialLabel={t("Close")}
    >
      {/* Header */}
      <Box
        flex
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "3rem"
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {t("Pause Membership")}
        </Typography>
      </Box>

      {numPauseRemaining < 1 && (
        <Typography variant="body1">
          {t("You can pause your membership")}{" "}
          <b>{t("up to two times per calendar year.")}</b>{" "}
          {t(
            "It looks like you’ve already used both pauses this year, so you won’t be able to pause again until next year."
          )}
        </Typography>
      )}

      {numPauseRemaining > 0 && (
        <Box>
          <Typography
            variant="body1"
            sx={{ display: "block", marginBottom: "3rem" }}
          >
            {t(
              "Your membership will pause the end of your current billing cycle."
            )}
          </Typography>
          {pausing && <CircularProgress />}

          {!pausing && (
            <Box>
              <Box
                sx={(theme: Theme) => ({
                  display: "flex",
                  flexDirection: "row",
                  gap: "2rem",
                  [theme.breakpoints.down("md")]: {
                    flexDirection: "column"
                  }
                })}
              >
                {/* Date range picker placeholder */}
                <Box
                  sx={(theme: Theme) => ({
                    display: "flex",
                    flexDirection: "column",
                    gap: "2rem",
                    [theme.breakpoints.down("md")]: {
                      flexDirection: "row"
                    }
                  })}
                >
                  <Box
                    sx={(theme: Theme) => ({
                      backgroundColor: theme.palette.primary[200],
                      paddingY: "0.4rem",
                      paddingX: "0.6rem",
                      borderRadius: "0.5rem"
                    })}
                  >
                    <Typography
                      variant="body2"
                      sx={(theme: Theme) => ({
                        color: theme.palette.primary[800]
                      })}
                    >
                      {t("{{numPauseRemaining}} Pauses remaining this year", {
                        numPauseRemaining
                      })}{" "}
                      <Icon icon={faInfoCircle} />
                    </Typography>
                  </Box>
                  <Box>
                    <TextField
                      label={t("Start")}
                      sx={{ width: "15rem" }}
                      disabled={true}
                      value={formatShortDate(nextBillingDate, timeZone, true)}
                    />
                  </Box>
                  <Box>
                    <TextField
                      label={t("End")}
                      sx={{ width: "15rem" }}
                      disabled={true}
                      value={
                        resumeDate
                          ? formatShortDate(resumeDate, timeZone, true)
                          : ""
                      }
                    />
                  </Box>
                </Box>

                {/* Calendar placeholder */}
                <Box>
                  <StaticDateRangePicker
                    calendars={1}
                    disablePast={true}
                    minDate={dayjs(nextBillingDate)}
                    maxDate={dayjs(nextBillingDate).add(3, "month")}
                    value={[
                      dayjs(nextBillingDate),
                      resumeDate ? dayjs(resumeDate) : null
                    ]}
                    rangePosition="end"
                    onChange={([_start, end]) => {
                      setResumeDate(end?.toDate() || null);
                    }}
                    slotProps={{
                      layout: {
                        sx: {
                          backgroundColor: "neutral.50",
                          borderRadius: "1.5rem",
                          minHeight: "200px"
                        }
                      },
                      actionBar: {
                        sx: {
                          display: "none"
                        }
                      },
                      toolbar: {
                        sx: {
                          display: "none"
                        }
                      }
                    }}
                  />
                </Box>
              </Box>

              {/* Info rows */}
              <Box
                sx={{
                  marginY: "3rem",
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem"
                }}
              >
                <Box
                  flex
                  sx={{
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: "0.5rem"
                  }}
                >
                  <Typography variant="body2" sx={{ color: "success.500" }}>
                    &bull;{" "}
                  </Typography>
                  <Typography variant="body2">
                    <strong>
                      {t("Existing enrollments will continue as scheduled")}
                    </strong>{" "}
                    {t(
                      "and any recurring payments will be charged to your saved payment method"
                    )}
                  </Typography>
                </Box>
                <Box
                  flex
                  sx={{
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: "0.5rem"
                  }}
                >
                  <Typography variant="body2" sx={{ color: "success.500" }}>
                    &bull;{" "}
                  </Typography>
                  <Typography variant="body2">
                    {t(
                      "When your membership resumes, you will get access to your credits and membership perks again."
                    )}{" "}
                    {t("Learn more")}
                  </Typography>
                </Box>
              </Box>

              {/* Action buttons */}
              <Box
                flex
                sx={{ flexDirection: "row", justifyContent: "space-between" }}
              >
                <Button variant="outlined" onClick={onClose}>
                  {t("Cancel")}
                </Button>
                <Button variant="contained" onClick={handleSave}>
                  {t("Save")}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </BasicModal>
  );
}
